import { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { projectsAPI, buyersAPI, brokersAPI } from '@/lib/api-client'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Loader2, Plus, X } from 'lucide-react'

const saleFormSchema = z.object({
  salePrice: z.number().positive('Valor deve ser maior que zero'),
  saleDate: z.string().min(1, 'Data da venda é obrigatória'),
  commissionRate: z.number().min(0).max(100).default(0),
  status: z.enum(['PENDING', 'SIGNED', 'PAID', 'CANCELLED']).default('PENDING'),
})

type SaleFormValues = z.infer<typeof saleFormSchema>

interface PermutaItem {
  assetType: string
  description: string
  value: number
}

const ASSET_TYPE_LABELS: Record<string, string> = {
  VEHICLE: 'Veículo',
  PROPERTY: 'Imóvel',
  LAND: 'Lote/Terreno',
  OTHER: 'Outro',
}

interface SaleFormDialogProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  buyerId: string
  sale?: any // When provided, dialog is in edit mode
}

function formatCurrency(value: number) {
  return new Intl.NumberFormat('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  }).format(value)
}

export function SaleFormDialog({
  open,
  onOpenChange,
  buyerId,
  sale,
}: SaleFormDialogProps) {
  const queryClient = useQueryClient()
  const isEditMode = !!sale

  const [selectedProjectId, setSelectedProjectId] = useState<string>('')
  const [selectedUnitId, setSelectedUnitId] = useState<string>('')
  const [saleChannel, setSaleChannel] = useState<'BROKER' | 'DEVELOPER'>('BROKER')
  const [selectedBrokerId, setSelectedBrokerId] = useState<string>('')

  // Permutas state
  const [permutas, setPermutas] = useState<PermutaItem[]>([])
  const [showPermutaForm, setShowPermutaForm] = useState(false)
  const [permutaType, setPermutaType] = useState<string>('VEHICLE')
  const [permutaDesc, setPermutaDesc] = useState('')
  const [permutaValue, setPermutaValue] = useState<number>(0)

  const form = useForm<SaleFormValues>({
    resolver: zodResolver(saleFormSchema),
    defaultValues: {
      salePrice: 0,
      saleDate: new Date().toISOString().split('T')[0],
      commissionRate: 0,
      status: 'PENDING',
    },
  })

  // Pre-fill form when editing
  useEffect(() => {
    if (sale && open) {
      form.reset({
        salePrice: Number(sale.salePrice) || 0,
        saleDate: sale.saleDate ? new Date(sale.saleDate).toISOString().split('T')[0] : '',
        commissionRate: Number(sale.commissionRate) || 0,
        status: sale.status || 'PENDING',
      })
      setSaleChannel(sale.saleChannel || 'BROKER')
      setSelectedBrokerId(sale.brokerId || '')
      if (sale.permutas?.length > 0) {
        setPermutas(sale.permutas.map((p: any) => ({
          assetType: p.assetType,
          description: p.description,
          value: Number(p.value),
        })))
      } else {
        setPermutas([])
      }
    }
  }, [sale, open])

  const salePrice = form.watch('salePrice')
  const commissionRate = form.watch('commissionRate')
  const commissionValue = salePrice && commissionRate ? (salePrice * commissionRate) / 100 : 0

  const totalPermutas = permutas.reduce((sum, p) => sum + p.value, 0)
  const valorEmDinheiro = (salePrice || 0) - totalPermutas

  // Fetch projects
  const { data: projectsData } = useQuery({
    queryKey: ['projects', { limit: 100 }],
    queryFn: () => projectsAPI.list({ limit: 100 }),
    enabled: open,
  })

  // Fetch units for selected project (only AVAILABLE)
  const { data: unitsData, isLoading: unitsLoading } = useQuery({
    queryKey: ['project-units', selectedProjectId, 'AVAILABLE'],
    queryFn: () =>
      projectsAPI.listUnits(selectedProjectId, {
        limit: 100,
        status: 'AVAILABLE',
      }),
    enabled: !!selectedProjectId,
  })

  // Fetch brokers
  const { data: brokersData } = useQuery({
    queryKey: ['brokers', { limit: 100, isActive: true }],
    queryFn: () => brokersAPI.list({ limit: 100, isActive: true }),
    enabled: open && saleChannel === 'BROKER',
  })

  const projects = projectsData?.data || []
  const units = unitsData?.data || []
  const brokersList = brokersData?.data || []

  // When a unit is selected, pre-fill price
  const selectedUnit = units.find((u: any) => u.id === selectedUnitId)
  useEffect(() => {
    if (selectedUnit?.price && form.getValues('salePrice') === 0) {
      form.setValue('salePrice', Number(selectedUnit.price))
    }
  }, [selectedUnit])

  // Reset unit when project changes
  useEffect(() => {
    setSelectedUnitId('')
  }, [selectedProjectId])

  // When broker is selected, pre-fill commissionRate
  useEffect(() => {
    if (selectedBrokerId) {
      const broker = brokersList.find((b: any) => b.id === selectedBrokerId)
      if (broker && form.getValues('commissionRate') === 0) {
        form.setValue('commissionRate', Number(broker.commissionRate))
      }
    }
  }, [selectedBrokerId])

  // Reset broker when channel changes to DEVELOPER
  useEffect(() => {
    if (saleChannel === 'DEVELOPER') {
      setSelectedBrokerId('')
      form.setValue('commissionRate', 0)
    }
  }, [saleChannel])

  const addPermuta = () => {
    if (!permutaDesc.trim() || permutaValue <= 0) {
      toast.error('Preencha a descrição e o valor da permuta')
      return
    }
    setPermutas([...permutas, { assetType: permutaType, description: permutaDesc.trim(), value: permutaValue }])
    setPermutaType('VEHICLE')
    setPermutaDesc('')
    setPermutaValue(0)
    setShowPermutaForm(false)
  }

  const removePermuta = (index: number) => {
    setPermutas(permutas.filter((_, i) => i !== index))
  }

  const createMutation = useMutation({
    mutationFn: async (values: SaleFormValues) => {
      return buyersAPI.createSale(buyerId, {
        unitId: selectedUnitId,
        salePrice: values.salePrice,
        saleDate: values.saleDate,
        saleChannel,
        brokerId: saleChannel === 'BROKER' && selectedBrokerId ? selectedBrokerId : undefined,
        commissionRate: saleChannel === 'BROKER' ? values.commissionRate : undefined,
        commission: saleChannel === 'BROKER' ? commissionValue : 0,
        status: values.status,
        permutas: permutas.length > 0 ? permutas : undefined,
      })
    },
    onSuccess: () => {
      toast.success('Venda registrada com sucesso!')
      handleClose()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: async (values: SaleFormValues) => {
      return buyersAPI.updateSale(buyerId, sale!.id, {
        salePrice: values.salePrice,
        saleDate: values.saleDate,
        saleChannel,
        brokerId: saleChannel === 'BROKER' && selectedBrokerId ? selectedBrokerId : null,
        commissionRate: saleChannel === 'BROKER' ? values.commissionRate : null,
        status: values.status,
        permutas: permutas.length > 0 ? permutas : [],
      })
    },
    onSuccess: () => {
      toast.success('Venda atualizada com sucesso!')
      handleClose()
    },
    onError: (error: Error) => {
      toast.error(error.message)
    },
  })

  const mutation = isEditMode ? updateMutation : createMutation

  const handleClose = () => {
    queryClient.invalidateQueries({ queryKey: ['buyer', buyerId] })
    queryClient.invalidateQueries({ queryKey: ['buyer', buyerId, 'financial-summary'] })
    queryClient.invalidateQueries({ queryKey: ['brokers'] })
    queryClient.invalidateQueries({ queryKey: ['assets'] })
    onOpenChange(false)
    form.reset()
    setSelectedProjectId('')
    setSelectedUnitId('')
    setSelectedBrokerId('')
    setSaleChannel('BROKER')
    setPermutas([])
  }

  const onSubmit = (values: SaleFormValues) => {
    if (!isEditMode && !selectedUnitId) {
      toast.error('Selecione uma unidade')
      return
    }
    mutation.mutate(values)
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{isEditMode ? 'Editar Venda' : 'Nova Venda'}</DialogTitle>
          <DialogDescription>
            {isEditMode
              ? 'Altere os dados da venda.'
              : 'Registre uma venda vinculada a este comprador.'}
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
          {/* Project & Unit Selection (create mode) or Read-only info (edit mode) */}
          {isEditMode ? (
            <div className="space-y-4">
              <h3 className="text-sm font-medium text-neutral-700">Unidade</h3>
              <div className="rounded-md bg-neutral-50 p-3 text-sm text-neutral-600">
                <span className="font-medium">{sale.unit?.code || '-'}</span>
                {' — '}
                {sale.unit?.type || '-'}, {sale.unit?.area ? `${Number(sale.unit.area)} m²` : '-'}
                {sale.unit?.price && (
                  <>, valor venal: {formatCurrency(Number(sale.unit.price))}</>
                )}
              </div>
            </div>
          ) : (
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Unidade
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Projeto *</Label>
                <Select
                  value={selectedProjectId}
                  onValueChange={setSelectedProjectId}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione um projeto" />
                  </SelectTrigger>
                  <SelectContent>
                    {projects.map((p: any) => (
                      <SelectItem key={p.id} value={p.id}>
                        {p.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label>Unidade *</Label>
                {unitsLoading ? (
                  <div className="flex items-center gap-2 h-10 text-sm text-neutral-500">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Carregando...
                  </div>
                ) : (
                  <Select
                    value={selectedUnitId}
                    onValueChange={setSelectedUnitId}
                    disabled={!selectedProjectId}
                  >
                    <SelectTrigger>
                      <SelectValue
                        placeholder={
                          !selectedProjectId
                            ? 'Selecione um projeto primeiro'
                            : units.length === 0
                            ? 'Nenhuma unidade disponível'
                            : 'Selecione uma unidade'
                        }
                      />
                    </SelectTrigger>
                    <SelectContent>
                      {units.map((u: any) => (
                        <SelectItem key={u.id} value={u.id}>
                          {u.code} - {u.type} ({Number(u.area)} m²)
                          {u.price ? ` - ${formatCurrency(Number(u.price))}` : ''}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                )}
              </div>
            </div>

            {selectedUnit && (
              <div className="rounded-md bg-neutral-50 p-3 text-sm text-neutral-600">
                <span className="font-medium">{selectedUnit.code}</span>
                {' — '}
                {selectedUnit.type}, {Number(selectedUnit.area)} m²
                {selectedUnit.price && (
                  <>, valor venal: {formatCurrency(Number(selectedUnit.price))}</>
                )}
              </div>
            )}
          </div>
          )}

          <Separator />

          {/* Sale Channel & Broker */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Canal de Venda
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label>Canal *</Label>
                <Select
                  value={saleChannel}
                  onValueChange={(v) => setSaleChannel(v as 'BROKER' | 'DEVELOPER')}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="BROKER">Corretor</SelectItem>
                    <SelectItem value="DEVELOPER">Incorporadora</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {saleChannel === 'BROKER' && (
                <div>
                  <Label>Corretor</Label>
                  <Select
                    value={selectedBrokerId}
                    onValueChange={setSelectedBrokerId}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Selecione um corretor" />
                    </SelectTrigger>
                    <SelectContent>
                      {brokersList.map((b: any) => (
                        <SelectItem key={b.id} value={b.id}>
                          {b.name} ({b.commissionRate}%)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>

          <Separator />

          {/* Sale Details */}
          <div className="space-y-4">
            <h3 className="text-sm font-medium text-neutral-700">
              Dados da Venda
            </h3>

            <div className="grid gap-4 sm:grid-cols-2">
              <div>
                <Label htmlFor="salePrice">Valor da Venda *</Label>
                <Input
                  id="salePrice"
                  type="number"
                  step="0.01"
                  {...form.register('salePrice', { valueAsNumber: true })}
                  placeholder="0,00"
                />
                {form.formState.errors.salePrice && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.salePrice.message}
                  </p>
                )}
              </div>

              <div>
                <Label htmlFor="saleDate">Data da Venda *</Label>
                <Input
                  id="saleDate"
                  type="date"
                  {...form.register('saleDate')}
                />
                {form.formState.errors.saleDate && (
                  <p className="text-sm text-destructive mt-1">
                    {form.formState.errors.saleDate.message}
                  </p>
                )}
              </div>

              {saleChannel === 'BROKER' && (
                <>
                  <div>
                    <Label htmlFor="commissionRate">Comissão (%)</Label>
                    <Input
                      id="commissionRate"
                      type="number"
                      step="0.01"
                      {...form.register('commissionRate', { valueAsNumber: true })}
                      placeholder="0,00"
                    />
                  </div>

                  <div>
                    <Label>Valor da Comissão</Label>
                    <div className="flex items-center h-10 px-3 rounded-md border bg-neutral-50 text-sm text-neutral-600">
                      {formatCurrency(commissionValue)}
                    </div>
                  </div>
                </>
              )}

              <div>
                <Label>Status</Label>
                <Select
                  value={form.watch('status')}
                  onValueChange={(value) =>
                    form.setValue('status', value as any)
                  }
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="PENDING">Pendente</SelectItem>
                    <SelectItem value="SIGNED">Assinado</SelectItem>
                    <SelectItem value="PAID">Pago</SelectItem>
                    <SelectItem value="CANCELLED">Cancelado</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          <Separator />

          {/* Permutas */}
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-medium text-neutral-700">Permutas</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={() => setShowPermutaForm(true)}
              >
                <Plus className="mr-1 h-3.5 w-3.5" />
                Adicionar Permuta
              </Button>
            </div>

            {/* Add permuta inline form */}
            {showPermutaForm && (
              <div className="rounded-lg border border-amber-200 bg-amber-50 p-4 space-y-3">
                <div className="grid gap-3 sm:grid-cols-[150px_1fr_150px]">
                  <div>
                    <Label className="text-xs">Tipo</Label>
                    <Select value={permutaType} onValueChange={setPermutaType}>
                      <SelectTrigger className="bg-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="VEHICLE">Veículo</SelectItem>
                        <SelectItem value="PROPERTY">Imóvel</SelectItem>
                        <SelectItem value="LAND">Lote/Terreno</SelectItem>
                        <SelectItem value="OTHER">Outro</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <Label className="text-xs">Descrição</Label>
                    <Input
                      className="bg-white"
                      placeholder="Ex: Honda Civic 2023, Lote 45..."
                      value={permutaDesc}
                      onChange={(e) => setPermutaDesc(e.target.value)}
                    />
                  </div>
                  <div>
                    <Label className="text-xs">Valor (R$)</Label>
                    <Input
                      className="bg-white"
                      type="number"
                      step="0.01"
                      placeholder="0,00"
                      value={permutaValue || ''}
                      onChange={(e) => setPermutaValue(Number(e.target.value))}
                    />
                  </div>
                </div>
                <div className="flex justify-end gap-2">
                  <Button type="button" variant="ghost" size="sm" onClick={() => setShowPermutaForm(false)}>
                    Cancelar
                  </Button>
                  <Button type="button" size="sm" onClick={addPermuta}>
                    Confirmar
                  </Button>
                </div>
              </div>
            )}

            {/* Permutas list */}
            {permutas.length > 0 && (
              <div className="space-y-2">
                {permutas.map((p, idx) => (
                  <div
                    key={idx}
                    className="flex items-center justify-between rounded-md border bg-neutral-50 px-3 py-2"
                  >
                    <div className="flex items-center gap-3 text-sm">
                      <span className="rounded bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-700">
                        {ASSET_TYPE_LABELS[p.assetType] || p.assetType}
                      </span>
                      <span className="text-neutral-700">{p.description}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <span className="text-sm font-medium text-amber-600">
                        {formatCurrency(p.value)}
                      </span>
                      <button
                        type="button"
                        onClick={() => removePermuta(idx)}
                        className="text-neutral-400 hover:text-red-500 transition-colors"
                      >
                        <X className="h-4 w-4" />
                      </button>
                    </div>
                  </div>
                ))}

                {/* Summary */}
                <div className="flex items-center justify-between rounded-md bg-neutral-100 px-3 py-2 text-sm">
                  <span className="text-neutral-600">
                    Total em permutas: <span className="font-medium text-amber-600">{formatCurrency(totalPermutas)}</span>
                  </span>
                  <span className="text-neutral-600">
                    Valor em dinheiro: <span className="font-medium text-green-600">{formatCurrency(Math.max(0, valorEmDinheiro))}</span>
                  </span>
                </div>
              </div>
            )}
          </div>

          <DialogFooter>
            <Button
              type="button"
              variant="outline"
              onClick={() => onOpenChange(false)}
            >
              Cancelar
            </Button>
            <Button
              type="submit"
              disabled={mutation.isPending || (!isEditMode && !selectedUnitId)}
            >
              {mutation.isPending && (
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              )}
              {isEditMode ? 'Salvar Alterações' : 'Registrar Venda'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}
